import React, { useCallback, useReducer, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { HoverForHelp, Icon } from "components/common";
import { ButtonGroup, Button, Badge } from "components/bootstrap";
import SelectedFiltersOverview from "search-filter/components/search-filter-bar/SelectedFiltersOverview";
import SearchFiltersOverview from "search-filter/components/search-filter-bar/SearchFiltersOverview";
import SearchFilterCreate from "search-filter/components/search-filter-bar/SearchFilterCreate";
import EffectiveQueryOverlay from "search-filter/components/search-filter-bar/EffectiveQueryOverlay";
import { SearchFilterExplanation } from "views/components/searchbar/SearchFilterBanner";
import useSearchFiltersFormState from "search-filter/hooks/useSearchFiltersFormState";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacings.sm};
  flex-direction: column;
  align-items: flex-start;
`;
const Controls = styled.div(
  ({ $displaySpacer }) => `
  display: flex;
  align-items: center;
  margin-right: ${$displaySpacer ? "5x" : 0};
`
);
const StyledButtonGroup = styled(ButtonGroup)`
  margin-left: 5px;
`;
const SearchFilterHelp = styled((props) => /* @__PURE__ */ React.createElement(HoverForHelp, { ...props }))`
  margin-left: 5px;
`;
const isBoolean = (value) => value === true || value === false;
const showPopoverReducer = (state, action) => {
  switch (action.type) {
    case "TOGGLE_CREATE_POPOVER":
      if (isBoolean(action.newState) && state.showCreatePopover === action.newState) {
        return state;
      }
      return {
        showCreatePopover: isBoolean(action.newState) ? action.newState : !state.showCreatePopover,
        showLoadPopover: false
      };
    case "TOGGLE_LOAD_POPOVER":
      if (isBoolean(action.newState) && state.showLoadPopover === action.newState) {
        return state;
      }
      return {
        showCreatePopover: false,
        showLoadPopover: isBoolean(action.newState) ? action.newState : !state.showLoadPopover
      };
    default:
      return state;
  }
};
const SearchFilterBar = ({ isParentMutable = true }) => {
  const selectedFilters = useSearchFiltersFormState();
  const sendTelemetry = useSendTelemetry("search-filter");
  const { t } = useTranslation("enterprise_search");
  const showAppliedFiltersButton = useMemo(() => (selectedFilters == null ? void 0 : selectedFilters.size) > 0, [selectedFilters]);
  const [showAppliedFilterList, setShowAppliedFilterList] = useState((selectedFilters == null ? void 0 : selectedFilters.size) > 0);
  const [{ showCreatePopover, showLoadPopover }, dispatchShowPopover] = useReducer(showPopoverReducer, {
    showCreatePopover: false,
    showLoadPopover: false
  });
  const toggleShowCreatePopover = useCallback(
    (newState) => {
      dispatchShowPopover({ type: "TOGGLE_CREATE_POPOVER", newState });
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_CREATE_CLICKED, {
        app_pathname: "search"
      });
    },
    [sendTelemetry]
  );
  const toggleShowLoadPopover = useCallback(
    (newState) => {
      dispatchShowPopover({ type: "TOGGLE_LOAD_POPOVER", newState });
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_LOAD_CLICKED, {
        app_pathname: "search"
      });
    },
    [sendTelemetry]
  );
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Controls, { $displaySpacer: !!(selectedFilters == null ? void 0 : selectedFilters.size) }, t("search_filter_bar.filters"), /* @__PURE__ */ React.createElement(SearchFilterHelp, { title: t("search_filter_bar.search_filters_help"), trigger: ["click"] }, /* @__PURE__ */ React.createElement(SearchFilterExplanation, null)), /* @__PURE__ */ React.createElement(StyledButtonGroup, null, /* @__PURE__ */ React.createElement(SearchFilterCreate, { showPopover: showCreatePopover, onChange: toggleShowCreatePopover }), /* @__PURE__ */ React.createElement(
    SearchFiltersOverview,
    {
      showPopover: showLoadPopover,
      onChange: toggleShowLoadPopover,
      isParentMutable
    }
  )), showAppliedFiltersButton && /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "transparent",
      bsSize: "sm",
      onClick: () => setShowAppliedFilterList((cur) => !cur),
      style: { marginLeft: 6 }
    },
    /* @__PURE__ */ React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } }, t("search_filter_bar.filters_count"), " ", /* @__PURE__ */ React.createElement(Badge, { bsStyle: "info", bsSize: "sm" }, selectedFilters == null ? void 0 : selectedFilters.size), /* @__PURE__ */ React.createElement(Icon, { name: showAppliedFilterList ? "keyboard_arrow_up" : "keyboard_arrow_down", size: "sm" }))
  ), /* @__PURE__ */ React.createElement(EffectiveQueryOverlay, null)), showAppliedFiltersButton && showAppliedFilterList && /* @__PURE__ */ React.createElement(SelectedFiltersOverview, { isParentMutable }));
};
export default SearchFilterBar;
